import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PILE_CHASSE_FIXE } from '../../theme/jetons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import PiedTableau from '../commun/PiedTableau';
import DialogueAjustementStock from './DialogueAjustementStock';
import { useLanguage } from '../../contexts/LanguageContext';
import { fileUrl } from '../../services/api';
import DocumentViewerModal, { typeDuFichier } from '../DocumentViewerModal';
import { productService } from '../../services/productService';
import { stockEntryService } from '../../services/stockEntryService';
import { stockExitService } from '../../services/stockExitService';
import { formatCurrency } from '../../utils/currency';
import { useFormatageDates } from '../../utils/dates';
import { numeroAffichable } from '../../utils/numeroCommande';

/**
 * La fiche produit — lot RU3-d.
 *
 * 🔴 UNE ROUTE, PAS UN DIALOGUE (`/products/:id`). Elle se partage par un lien,
 * elle se recharge, et le bouton « précédent » du navigateur y fonctionne. Un
 * dialogue n'a aucune de ces trois propriétés, et ce sont exactement celles
 * qu'on attend d'une page qu'on ouvre pour COMPRENDRE quelque chose.
 *
 * 🔴 ET IL N'Y A PAS DE COLONNE « STOCK APRÈS », pour DEUX raisons devenues
 * indépendantes — l'une suffirait :
 *   1. ni `StockEntry` ni `StockExit` ne portent d'état après opération, et
 *      `current_stock` a trois écrivains qui ne laissent aucun mouvement ;
 *   2. **le stock INITIAL n'est pas un mouvement.** L'invariant est
 *      `stock initial + Σentrées − Σsorties`, et le premier terme n'est
 *      enregistré nulle part. Même avec des mouvements parfaits, la
 *      reconstruction n'aurait pas d'origine.
 * Le serveur le dit lui-même : `stock_apres_disponible: false`.
 */

/**
 * ⚠️ « VALEUR DU STOCK » REPOSE SUR UN CHIFFRE QUI PEUT MENTIR — question posée
 * AVANT d'écrire, pas découverte après.
 *
 * `current_stock` a été réécrit à tort par le formulaire jusqu'au correctif de
 * RU3-c : le produit 7 de la base de développement porte aujourd'hui 35
 * d'écart avec son propre historique. La valeur affichée ici est donc
 * `stock × prix` d'un stock qui, sur des données antérieures au correctif,
 * peut être faux.
 *
 * 🔴 CE QUE LA VUE MONTRE SI LE STOCK ET L'HISTORIQUE SE CONTREDISENT : RIEN.
 * Et ce n'est pas un choix de prudence, c'est une impossibilité — **la
 * contradiction n'est pas détectable d'ici**. La détecter demanderait de
 * comparer `current_stock` à `stock initial + Σentrées − Σsorties`, et le
 * stock initial n'existe dans aucune table. Afficher un signe supposerait donc
 * une règle de détection qui ne peut pas être juste : elle crierait sur tout
 * produit créé avec un stock de départ, c'est-à-dire sur presque tous.
 *
 * *Un écran qui alerte à tort apprend à ignorer ses alertes.* La question se
 * rouvrira le jour où le stock initial sera enregistré comme une ligne — c'est
 * la même condition que la colonne « stock après », et c'est consigné.
 */

const DialogueDetailProduit = ({
  id, ouvert, onFermer, onModifier,
  showSnackbar, currentStock, refreshUnreadNotifications,
}) => {
  /**
   * 🔴 L'IMAGE N'ÉTAIT PAS CLIQUABLE — ni `onClick`, ni `cursor` : rien
   * n'était câblé. Une vignette de 200 px est trop petite pour lire une
   * étiquette ou un numéro de série, qui sont **exactement** ce qu'on vient
   * chercher sur la photo d'un article.
   * ⚠️ `cursor: zoom-in` PLUTÔT QUE `pointer` : les deux rendent l'élément
   * cliquable, mais l'un annonce une navigation et l'autre un agrandissement.
   * *Le curseur est la seule promesse faite avant le clic.*
   */
  const [imageAgrandie, setImageAgrandie] = useState(false);

  const { t } = useLanguage();
  const { formaterHorodatage } = useFormatageDates();

  const [produit, setProduit] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [mouvements, setMouvements] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [parPage, setParPage] = useState(20);
  const [erreurMouvements, setErreurMouvements] = useState(false);

  // ⚠️ UN SEUL COUPLE page/parPage POUR LES DEUX ONGLETS, remis à 1 au
  // changement : deux paginations indépendantes donneraient un pied de tableau
  // qui annonce le total de l'onglet qu'on ne regarde pas.
  const [onglet, setOnglet] = useState('mouvements');
  const [commandes, setCommandes] = useState([]);
  const [totalCommandes, setTotalCommandes] = useState(0);
  const [pagesCommandes, setPagesCommandes] = useState(1);
  const [erreurCommandes, setErreurCommandes] = useState(false);

  const [ajustementOuvert, setAjustementOuvert] = useState(false);
  const [ajustementEnCours, setAjustementEnCours] = useState(false);

  const chargerProduit = useCallback(async () => {
    if (!ouvert || !id) return;
    try {
      const reponse = await productService.getProduct(id);
      setProduit(reponse.product || reponse);
      setErreur(null);
    } catch (e) {
      // ⚠️ 404 ET 403 MÈNENT AU MÊME ÉCRAN, et c'est voulu : le serveur rend
      // 404 pour une ressource d'une autre entreprise précisément pour ne pas
      // confirmer son existence. Distinguer les deux ici rétablirait côté
      // client l'oracle que la route refuse de donner.
      setErreur('introuvable');
    } finally {
      setChargement(false);
    }
  }, [id, ouvert]);

  const chargerMouvements = useCallback(async () => {
    if (!ouvert || !id) return;
    try {
      const reponse = await productService.getProductMovements(id, {
        page, per_page: parPage,
      });
      setMouvements(reponse.movements || []);
      setTotal(reponse.total || 0);
      setPages(reponse.pages || 1);
      setErreurMouvements(false);
    } catch (e) {
      // 🔴 L'HISTOIRE ÉCHOUE SÉPARÉMENT DE LA FICHE. Un historique en erreur ne
      // doit pas faire disparaître les quatre chiffres : ce sont deux
      // requêtes, ce sont deux échecs, et un écran vide n'apprendrait rien.
      setMouvements([]);
      setErreurMouvements(true);
    }
  }, [id, ouvert, page, parPage]);

  const chargerCommandes = useCallback(async () => {
    if (!ouvert || !id) return;
    try {
      const reponse = await productService.getProductPurchaseOrders(id, {
        page, per_page: parPage,
      });
      setCommandes(reponse.items || []);
      setTotalCommandes(reponse.total || 0);
      setPagesCommandes(reponse.pages || 1);
      setErreurCommandes(false);
    } catch (e) {
      setCommandes([]);
      setErreurCommandes(true);
    }
  }, [id, ouvert, page, parPage]);

  useEffect(() => { chargerProduit(); }, [chargerProduit]);
  // ⚠️ ON NE CHARGE QUE L'ONGLET VISIBLE. Charger les deux à l'ouverture
  // ferait deux requêtes pour un écran qui n'en montre qu'une — et la seconde
  // serait périmée au moment où l'on bascule.
  useEffect(() => {
    if (onglet === 'mouvements') chargerMouvements(); else chargerCommandes();
  }, [onglet, chargerMouvements, chargerCommandes]);

  // ⚠️ Le stock change → l'historique aussi. Les deux se rechargent ensemble,
  // sinon on verrait le nouveau chiffre au-dessus de l'ancienne liste — le
  // même désaccord écran/donnée que le correctif de RU3-c vient de fermer.
  const handleValiderAjustement = async ({ type, quantite, motif }) => {
    setAjustementEnCours(true);
    try {
      const charge = {
        product_id: produit.id,
        quantity: quantite,
        unit_price: produit.price,
      };
      if (type === 'entree') {
        await stockEntryService.createStockEntry({ ...charge, reference: motif });
      } else {
        await stockExitService.createStockExit({ ...charge, reason: motif });
      }
      setAjustementOuvert(false);
      showSnackbar(t('messages.stockAdjusted'), 'success');
      setPage(1);
      await Promise.all([chargerProduit(), chargerMouvements()]);
      if (refreshUnreadNotifications) refreshUnreadNotifications();
    } catch (e) {
      showSnackbar(t('messages.stockAdjustError'), 'error');
    } finally {
      setAjustementEnCours(false);
    }
  };

  /* 🔴 UN DIALOGUE, PAS UNE PAGE — ET L'URL RESTE. La liste demeure derrière :
     on n'a pas quitté son filtre, sa page ni son défilement. L'adresse
     `/products/:id` change quand même, donc un lien se partage — et le bouton
     « précédent » du navigateur ramène à `/products`, ce qui FERME le dialogue
     au lieu de quitter l'écran. C'est le parent qui route ; ce composant ne
     connaît que `ouvert` et `onFermer`.
     ⚠️ Et l'entête n'est plus `EnteteEcran` : ce composant est réservé aux
     ÉCRANS de travail (décision RU2). Un dialogue a son propre titre. */
  const coquille = (contenu) => (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="lg" fullWidth>
      {contenu}
    </Dialog>
  );

  if (chargement) {
    return coquille(
      <DialogContent>
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      </DialogContent>,
    );
  }

  if (erreur || !produit) {
    return coquille(
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>{t('products.notFound')}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onFermer}>
          {t('products.backToList')}
        </Button>
      </DialogContent>,
    );
  }

  const stockBas = Boolean(produit.is_low_stock);
  const epuise = produit.current_stock === 0;
  const devise = produit.currency || currentStock?.currency || 'EUR';

  // ⚠️ LES QUATRE CHIFFRES, ET UN SEUL SE TEINTE. `is_low_stock` est LU, jamais
  // recopié : c'est la décision 10, et le serveur porte la clause `min_stock > 0`
  // qu'une recopie oubliait à trois endroits.
  const chiffres = [
    {
      cle: 'stock',
      libelle: t('products.currentStock'),
      valeur: produit.current_stock,
      couleur: !stockBas ? 'text.primary' : epuise ? 'error.main' : 'warning.main',
      teinte: stockBas,
    },
    { cle: 'seuil', libelle: t('common.minStock'), valeur: produit.min_stock },
    {
      cle: 'prix',
      libelle: t('products.unitPrice'),
      valeur: produit.price != null ? formatCurrency(produit.price, devise) : '-',
    },
    {
      cle: 'valeur',
      libelle: t('products.stockValue'),
      valeur: produit.price != null
        ? formatCurrency(produit.price * produit.current_stock, devise)
        : '-',
    },
  ];

  return coquille(
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
                   alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="span" display="block" noWrap>
              {produit.name}
            </Typography>
            {/* Chasse fixe, comme dans le tableau : un code-barres se compare
                chiffre à chiffre. */}
            <Typography variant="caption" color="text.secondary"
                        sx={{ fontFamily: PILE_CHASSE_FIXE }}>
              {produit.barcode}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<SwapVertIcon />}
              onClick={() => setAjustementOuvert(true)}
            >
              {t('products.adjust')}
            </Button>
            {/* 🔴 « MODIFIER » FERME LE DÉTAIL ET OUVRE LE FORMULAIRE — il ne
                l'empile pas. Deux dialogues superposés rendraient le retour
                ambigu : « précédent » fermerait lequel ? Et le formulaire n'est
                toujours PAS dupliqué ici : il vit dans la liste, seule source. */}
            <Button variant="contained" startIcon={<EditIcon />} onClick={onModifier}>
              {t('products.update')}
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start', mb: 3 }}>
        {/* ── L'EMPLACEMENT PHOTO, À GAUCHE ──────────────────────────────────
            ⚠️ Il occupe sa place MÊME VIDE. Un emplacement qui n'apparaît que
            lorsqu'il est rempli fait sauter toute la mise en page au premier
            envoi d'image, et personne ne devine qu'on POUVAIT en mettre une. */}
        <Paper
          variant="outlined"
          sx={{
            width: 200, height: 200, flexShrink: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 1,
            overflow: 'hidden',
          }}
        >
          {produit.image_path ? (
            <Box
              component="img"
              /* ⚠️ `fileUrl` ET NON UN CHEMIN ÉCRIT À LA MAIN : les fichiers
                 sont servis par une route SCOPÉE (`scoped_file`), pas depuis
                 `/uploads`. Un chemin en dur rendrait une image cassée — et
                 seulement pour les images, donc personne ne le verrait avant
                 le premier produit photographié. */
              src={fileUrl(produit.image_path)}
              alt={produit.name}
              onClick={() => setImageAgrandie(true)}
              title={t('products.clickToView')}
              sx={{ width: '100%', height: '100%', objectFit: 'cover',
                    cursor: 'zoom-in' }}
            />
          ) : (
            <>
              <PhotoCameraIcon color="disabled" />
              <Typography variant="caption" color="text.secondary">
                {t('products.noPhoto')}
              </Typography>
              <Button
                size="small"
                onClick={onModifier}
              >
                {t('products.addPhoto')}
              </Button>
            </>
          )}
        </Paper>

        <Box
          sx={{
            flex: '1 1 320px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 2,
          }}
        >
          {chiffres.map((c) => (
            <Paper
              key={c.cle}
              variant="outlined"
              sx={{
                p: 2,
                // ⚠️ MÊME MÉCANIQUE QUE LA LIGNE TEINTÉE DE RU3-a, et les mêmes
                // deux opacités par gravité : `alpha()` sur un jeton, jamais une
                // couleur figée, et 0,24 pour l'épuisé contre 0,13 pour le bas.
                // À opacité commune, les deux teintes ne se distinguent pas —
                // 13,5 points de L* d'écart à la source.
                ...(c.teinte ? {
                  backgroundColor: (theme) => alpha(
                    theme.palette[epuise ? 'error' : 'warning'].main,
                    theme.palette.mode === 'dark' ? 0.13 : (epuise ? 0.24 : 0.13),
                  ),
                } : {}),
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {c.libelle}
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: c.couleur }}>
                {c.valeur}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* ⚠️ DEUX ONGLETS, PAS DEUX SECTIONS EMPILÉES. Ce sont deux réponses à
          deux questions — « qu'est-il arrivé à mon stock » et « chez qui l'ai-je
          acheté » — et on n'en pose qu'une à la fois. Empilées, la seconde
          serait sous un historique paginé, donc invisible. */}
      <Tabs
        value={onglet}
        onChange={(e, v) => { setOnglet(v); setPage(1); }}
        sx={{ mb: 2 }}
      >
        <Tab value="mouvements" label={t('products.movementHistory')} />
        <Tab value="commandes" label={t('navigation.orders')} />
      </Tabs>

      {onglet === 'mouvements' ? (
      <>
      {erreurMouvements && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('products.movementsError')}</Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('products.movementDate')}</TableCell>
              <TableCell>{t('products.movementType')}</TableCell>
              <TableCell align="right">{t('common.quantity')}</TableCell>
              <TableCell>{t('products.reason')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mouvements.length === 0 && !erreurMouvements && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('products.noMovements')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {mouvements.map((m) => (
              <TableRow key={`${m.type}-${m.id}`} hover>
                <TableCell>{formaterHorodatage(m.created_at)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={m.type === 'entree' ? t('products.entry') : t('products.exit')}
                    color={m.type === 'entree' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                {/* 🔴 LA QUANTITÉ EST SIGNÉE, et le signe vient du TYPE servi
                    par le serveur — jamais d'une comparaison refaite ici. Un
                    « 10 » nu dans une liste qui mêle entrées et sorties oblige
                    à lire deux colonnes pour comprendre une ligne. */}
                <TableCell
                  align="right"
                  sx={{ color: m.type === 'entree' ? 'success.main' : 'error.main' }}
                >
                  {m.type === 'entree' ? '+' : '−'}{m.quantity}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {m.motif || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      </>
      ) : (
      <>
      {erreurCommandes && (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('products.ordersError')}</Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('products.orderNumber')}</TableCell>
              <TableCell>{t('products.movementDate')}</TableCell>
              <TableCell>{t('products.supplier')}</TableCell>
              <TableCell align="right">{t('products.orderedQty')}</TableCell>
              <TableCell align="right">{t('products.receivedQty')}</TableCell>
              {/* 🔴 LE PRIX D'ACHAT DE **CETTE LIGNE**, pas le prix catalogue du
                  produit ni le total de la commande. Mesuré en base : les deux
                  diffèrent toujours, et le même article a été acheté à deux
                  prix selon la commande. C'est la question que l'onglet
                  répond — « à quel prix la dernière fois ». */}
              <TableCell align="right">{t('products.purchasePrice')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {commandes.length === 0 && !erreurCommandes && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('products.noOrders')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {/* ⚠️ LA CLÉ EST L'ID DE LA **LIGNE**, jamais celui de la commande :
                un même produit peut figurer deux fois dans la même commande —
                mesuré en base. Une clé par commande en écraserait une. */}
            {commandes.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>
                  <Typography variant="body2">{numeroAffichable(l.order.order_number)}</Typography>
                  <Chip size="small" variant="outlined" label={l.order.status} sx={{ mt: 0.5 }} />
                </TableCell>
                <TableCell>{formaterHorodatage(l.order.order_date)}</TableCell>
                <TableCell>{l.order.supplier_name || '—'}</TableCell>
                <TableCell align="right">{l.quantity}</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={l.remaining_quantity > 0 ? 'warning.main' : 'text.primary'}
                  >
                    {l.received_quantity}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(l.unit_price, devise)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </>
      )}

      <PiedTableau
        total={onglet === 'mouvements' ? total : totalCommandes}
        page={page}
        pages={onglet === 'mouvements' ? pages : pagesCommandes}
        parPage={parPage}
        onPage={setPage}
        onParPage={(n) => { setParPage(n); setPage(1); }}
        libelleTotal={`${onglet === 'mouvements' ? total : totalCommandes} `
          + `${(onglet === 'mouvements' ? t('products.movementHistory') : t('navigation.orders')).toLowerCase()}`}
        libelleParPage={t('common.perPage')}
      />

      </DialogContent>

      {/* 🔴 DEUX DIALOGUES EMPILÉS, ET C'EST LE SEUL CAS ADMIS. L'ajustement se
          superpose au détail ; fermer l'ajustement ne ferme QUE l'ajustement,
          parce que chacun porte son propre `open` et son propre `onClose` —
          aucun état partagé. MUI ne remet la touche Échap qu'au dialogue du
          dessus, donc l'ordre de fermeture suit l'ordre d'ouverture.
          ⚠️ ET LE DÉTAIL SE RAFRAÎCHIT APRÈS L'AJUSTEMENT : `handleValider`
          relit le produit ET l'historique. Sans cela on rejouerait exactement
          le défaut corrigé au correctif de RU3-c — un écran qui affiche encore
          l'ancien stock après avoir annoncé que l'ajustement a réussi. */}
      <DialogueAjustementStock
        produit={produit}
        ouvert={ajustementOuvert}
        enCours={ajustementEnCours}
        onFermer={() => setAjustementOuvert(false)}
        onValider={handleValiderAjustement}
      />

      {/* 🔴 LE MÊME VISUALISEUR QUE LE PDF ET LES PIÈCES JOINTES — quatrième
          et dernière surface. Elle n'ouvrait rien du tout ; les trois autres
          ouvraient un onglet. *Quatre façons de regarder un fichier, dont une
          qui n'existait pas.* */}
      <DocumentViewerModal
        open={imageAgrandie}
        onClose={() => setImageAgrandie(false)}
        url={produit?.image_path ? `/files/${produit.image_path}` : null}
        title={produit?.name || ''}
        filename={produit?.image_path}
        mimeType={typeDuFichier(produit?.image_path).mimeType}
        estImage={typeDuFichier(produit?.image_path).estImage}
      />
    </>,
  );
};

export default DialogueDetailProduit;
